/**
 * Buscar en el catálogo de productos, licencias y servicios.
 *
 * Vive suelto y sin importar nada, como `coincidencia.js`: el componente que
 * lo usa importa `@/App` para el axios configurado, y probar un filtro no
 * puede arrastrar la aplicación entera.
 *
 * Por qué existe
 * --------------
 * Angel, 15-sep-2026: "un buscador de la parte de productos, licencias y
 * servicios, porque la lista es grande y es poco usable". Lo que había era
 * elegir el tipo en un desplegable y luego el producto en otro desplegable
 * con la lista entera de ese tipo.
 *
 * El catálogo ya viene entero del servidor (`GET /products`), así que se
 * filtra aquí, sin más peticiones. Distinto del buscador de clientes, que
 * pregunta al servidor porque son 2.000.
 */

const TIPO = {
  license: { emoji: '🔑', nombre: 'Licencia' },
  equipment: { emoji: '📦', nombre: 'Equipo' },
  service: { emoji: '🛠️', nombre: 'Servicio' },
  renting: { emoji: '📄', nombre: 'Renting' },
};

/**
 * NADA se excluye de este buscador por su tipo.
 *
 * El 24-sep-2026 quedó fuera el tipo `renting`, y fue un error: dejó los
 * productos a plazos sin poder usarse en una factura a mano, que es justo lo
 * que Angel notó al día siguiente. El renting no es lo que un producto ES,
 * sino cómo se PAGA: en una factura ese TPV se vende, y se vende a su precio.
 *
 * Lo que no se puede facturar es la CUOTA de un renting, y eso se decide por
 * línea (`renting_meses`), no por producto. Ver `lib/renting.js`.
 */

/** Sin tildes ni mayúsculas: "Módulo" encuentra "modulo" y al revés. */
export function normalizar(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** Cómo se enseña el tipo: emoji y nombre en cristiano. */
export function etiquetaDeTipo(tipo) {
  return TIPO[tipo] || { emoji: '📄', nombre: tipo || 'Otro' };
}

/**
 * Los productos que casan con lo escrito, por referencia, nombre, descripción
 * o tipo. Cada palabra tiene que estar en algún campo: "licencia tpv"
 * encuentra "Licencia Camarero10 TPV" aunque no vayan seguidas.
 *
 * Sin texto devuelve el catálogo entero. OJO: eso NO significa que el
 * buscador lo enseñe al abrirse. Enfocar el cuadro ya no despliega nada,
 * porque ese panel tapaba la tabla de líneas y se comía los clics que iban
 * al concepto (ver `ProductSearchSelect`). El catálogo entero solo se pide
 * a propósito, con la flecha abajo.
 *
 * Los inactivos no salen: no se venden.
 */
export function filtrarProductos(productos, texto, limite = 40) {
  const activos = (productos || []).filter((p) => p && p.is_active !== false);
  const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return activos.slice(0, limite);

  const casa = (p) => {
    const pajar = normalizar([
      p.ref, p.name, p.description, etiquetaDeTipo(p.type).nombre, p.type,
    ].filter(Boolean).join(' '));
    return palabras.every((palabra) => pajar.includes(palabra));
  };
  return activos.filter(casa).slice(0, limite);
}

export default filtrarProductos;
